import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react"
import { Field, Form, Formik } from "formik"
import { useMemo, useState } from "react"
import * as Yup from 'yup'
import { ComplainFieldType } from "../utils/types"
import clsx from "clsx"
import Spinner from "./spinner"
import { request } from "../utils/request"
import { api } from "../utils/api"
import { Button } from "./button"

interface ComplainProps {
    id: number;
    onRaised?: (values: ComplainFieldType) => void
}

export default function Complain(props: ComplainProps) {
    const [visible, setVisible] = useState(false)
    const open = () => {
        setVisible(true)
    }
    const initialValues: ComplainFieldType = useMemo(() => ({ complain_title: '', complain_description: '' }), []);

    const close = () => {
        setVisible(false)
    }

    return (
        <>
            <Button onClick={open}>Raise a Complain</Button>
            <Dialog open={visible} onClose={close} className="relative z-10">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-900 opacity-25 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                />
                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <DialogPanel
                            transition
                            className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                        >
                            <h2 className="mt-5 text-center text-2xl font-bold leading-9 tracking-tight text-gray-900">
                                Raise a complain
                            </h2>
                            <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-5 lg:px-8">
                                <div className="sm:mx-auto sm:w-full sm:max-w-sm">
                                    <Formik
                                        initialValues={initialValues}
                                        validationSchema={validationSchema}
                                        onSubmit={async (values) => {
                                            try {
                                                await request('POST', api.locksmith.complain, { ...values, job_id: props.id })
                                                close()
                                                props.onRaised?.(values)
                                            } catch (error) {
                                                console.log("🚀 ~ onSubmit={ ~ error:", error)
                                            }
                                        }}
                                    >
                                        {helpers => (
                                            <Form>
                                                <label htmlFor="complain_title" className={
                                                    clsx("text-sm font-medium leading-6",
                                                        { 'text-red-600': helpers.errors.complain_title },
                                                        { 'text-gray-900': !helpers.errors.complain_title })
                                                }>
                                                    Complain Title<label className="text-sm font-large leading-6 text-red-500">{helpers.errors.complain_title && '*'}</label>
                                                </label>
                                                <Field
                                                    className="block w-full px-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                                                    placeholder="e.g. title"
                                                    name="complain_title" />
                                                <div className='mt-2' />
                                                <label htmlFor="complain_description" className={
                                                    clsx("text-sm font-medium leading-6",
                                                        { 'text-red-600': helpers.errors.complain_description },
                                                        { 'text-gray-900': !helpers.errors.complain_description })
                                                }>
                                                    Complain Description<label className="text-sm font-large leading-6 text-red-500">{helpers.errors.complain_description && '*'}</label>
                                                </label>
                                                <Field
                                                    as={'textarea'}
                                                    rows={4}
                                                    placeholder="Describe complain..."
                                                    className="block resize-none w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                                                    name="complain_description" />
                                                {
                                                    !helpers.isSubmitting ?
                                                        <button
                                                            className="mt-5 flex w-full justify-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                                                            type="submit" disabled={helpers.isSubmitting}>
                                                            Submit
                                                        </button> : <div className="flex justify-center items-center">
                                                            <Spinner />
                                                        </div>
                                                }
                                            </Form>
                                        )}
                                    </Formik>
                                </div>
                            </div>
                        </DialogPanel>
                    </div>
                </div >
            </Dialog>
        </>
    )
}

const validationSchema = Yup.object().shape({
    complain_title: Yup.string().required('Required!'),
    complain_description: Yup.string().required('Required!')
})
